import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { TriangleIcon } from "@icons";
import styles from "./ReportCollapsibleSection.module.scss";

interface ReportCollapsibleSectionProps {
    title: string;
    count?: number;
    trailingValue?: string;
    defaultOpen?: boolean;
    indentLevel?: "section" | "subsection";
    children?: React.ReactNode;
}

export const ReportCollapsibleSection: React.FC<
    ReportCollapsibleSectionProps
> = ({
    title,
    count,
    trailingValue,
    defaultOpen = true,
    indentLevel = "section",
    children,
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const hasChildren = Boolean(children);
    const toggleOpen = useCallback(() => {
        if (hasChildren) {
            setIsOpen((current) => !current);
        }
    }, [hasChildren]);

    return (
        <section
            className={classNames(styles.section, {
                [styles.subsection]: indentLevel === "subsection",
            })}
        >
            <div className={styles.headerRow}>
                <button
                    type="button"
                    className={styles.header}
                    onClick={toggleOpen}
                    disabled={!hasChildren}
                >
                    {hasChildren ? (
                        <TriangleIcon
                            className={classNames(styles.icon, {
                                [styles.iconClosed]: !isOpen,
                            })}
                        />
                    ) : (
                        <span className={styles.iconPlaceholder} />
                    )}
                    <span className={styles.title}>{title}</span>
                    {count !== undefined ? (
                        <span className={styles.count}>{count}</span>
                    ) : null}
                </button>
                {!isOpen && trailingValue ? (
                    <span className={styles.trailingValue}>
                        {trailingValue}
                    </span>
                ) : null}
            </div>
            {hasChildren && isOpen ? (
                <div className={styles.content}>{children}</div>
            ) : null}
        </section>
    );
};
